import React from "react";
import { Button } from "./ui/button";
import { AlertTriangle, RotateCcw } from "lucide-react";

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Global error boundary — a render crash must never leave the installed PWA
 * on a dead white screen. Shows a recoverable error state instead and logs
 * the real error so it can be diagnosed.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] App crashed:", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[100dvh] w-full flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 px-6 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h1 className="text-lg font-black text-slate-900 dark:text-white mb-2">Algo correu mal</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 max-w-xs">
          A aplicação encontrou um erro inesperado. Podes tentar recarregar.
        </p>
        {this.state.error?.message && (
          <p className="text-[11px] font-mono text-slate-400 dark:text-slate-600 mb-6 max-w-sm break-words">
            {this.state.error.message}
          </p>
        )}
        <Button
          onClick={() => window.location.reload()}
          className="bg-brand hover:opacity-90 text-white rounded-xl h-11 px-6 font-bold"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Recarregar
        </Button>
      </div>
    );
  }
}
